import React, { useState } from 'react';
import { Lock, User, LogOut, ClipboardList } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingStates';
import PromemoriaArea from './PromemoriaArea';

/**
 * Area PROMEMORIA autonoma, separata dalla Social Dashboard.
 * Ha una propria pagina di login (stesse credenziali della dashboard, ma accesso
 * dedicato e senza la chrome della dashboard). Pensata per stare su un sottodominio.
 * Accesso riservato agli admin.
 */

const RemindersLogin: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Inserisci email e password');
      return;
    }
    const ok = await login(email, password);
    if (!ok) toast.error('Credenziali non valide');
  };

  return (
    <div className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden p-4" style={{ backgroundColor: '#FAFAFB' }}>
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at top, rgba(19,22,28,0.03) 0%, transparent 60%)' }}
        aria-hidden="true"
      />
      <motion.div
        className="relative w-full max-w-[360px]"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-[10px]" style={{ backgroundColor: '#F4F5F7', border: '1px solid #DDE0E6' }}>
            <ClipboardList className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-medium" style={{ color: '#13161C', letterSpacing: '-0.2px' }}>
            Promemoria
          </h1>
          <p className="mt-2 text-sm" style={{ color: '#5B6270' }}>
            Area riservata · accedi per continuare
          </p>
        </div>

        <div className="rounded-[10px] p-7" style={{ backgroundColor: '#FFFFFF', border: '1px solid #EBEDF1' }}>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <User className="h-5 w-5 text-slate-500" />
              </div>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full rounded-xl border py-3.5 pl-12 pr-4 text-sm focus:outline-none"
                style={{ backgroundColor: '#F4F5F7', color: '#13161C', borderColor: '#DDE0E6' }}
              />
            </div>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <Lock className="h-5 w-5 text-slate-500" />
              </div>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full rounded-xl border py-3.5 pl-12 pr-4 text-sm focus:outline-none"
                style={{ backgroundColor: '#F4F5F7', color: '#13161C', borderColor: '#DDE0E6' }}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-xl py-3.5 text-sm font-semibold transition-all duration-200 hover:-translate-y-px active:scale-[0.98] disabled:opacity-50"
              style={{ backgroundColor: '#FDF751', color: '#13161C' }}
            >
              {isLoading ? 'Accesso…' : 'Accedi'}
            </button>
          </form>
        </div>

        <p className="mt-8 text-center text-[10px] font-medium uppercase tracking-[0.2em]" style={{ color: '#8B92A0' }}>
          ByMarke · Promemoria
        </p>
      </motion.div>
    </div>
  );
};

const RemindersApp: React.FC = () => {
  const { user, isLoading, logout } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3" style={{ backgroundColor: '#FAFAFB' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Non loggato -> login dedicato.
  if (!user) return <RemindersLogin />;

  // Loggato ma non admin -> accesso negato (non è la sua area).
  if (user.role !== 'admin') {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 p-6 text-center" style={{ backgroundColor: '#FAFAFB' }}>
        <div className="text-5xl">🔒</div>
        <p className="text-slate-900">Area riservata agli amministratori.</p>
        <button onClick={logout} className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-black">
          Esci
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-white dark:bg-gray-950">
      {/* Header minimale dell'app Promemoria (nessuna sidebar dashboard) */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-gray-800 dark:bg-gray-950/90">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 dark:bg-white">
              <ClipboardList className="h-4 w-4 text-white dark:text-slate-900" />
            </div>
            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Promemoria</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-slate-500 dark:text-slate-400 sm:inline">{user.username}</span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
            >
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main>
        <PromemoriaArea />
      </main>
    </div>
  );
};

export default RemindersApp;
